<template>
  <v-sheet
    class="v-application v-application--is-ltr vuetify-wrapper"
    content-class="v-application v-application--is-ltr vuetify-wrapper"
  >
    <v-btn
      v-if="!isEditMode"
      color="primary"
      small
      @click.stop="isDrawerOpen = !isDrawerOpen"
      class="add-appointment"
    >
      <v-icon>mdi-plus</v-icon>
      Termin Anlegen
    </v-btn>
    <v-btn
      v-if="isEditMode"
      dark
      class="edit-appointment"
      @click.stop="isDrawerOpen = !isDrawerOpen"
    >
      <v-icon small>mdi-pencil</v-icon>
    </v-btn>

    <v-alert
      v-if="proposalWarning"
      type="warning"
      outlined
      class="proposal-initialization-warning mt-3"
      style="max-width: 600px"
      role="alert"
    >
      {{ proposalWarning.message }}
      <v-btn class="resume-appointment-proposals mt-2" small :loading="resumingProposals" @click="resumeProposals">
        Vorschläge erneut starten
      </v-btn>
    </v-alert>

    <v-navigation-drawer
      v-model="isDrawerOpen"
      width="680px"
      overlay-opacity="0.8"
      fixed
      temporary
      right
    >
      <v-form ref="form" v-model="valid">
        <v-container class="apt-manager" fluid>
          <v-row>
            <v-col cols="12" class="py-6">
              <h3 v-if="!isEditMode">Neuen Termin anlegen</h3>
              <h3 v-if="isEditMode">Termin bearbeiten</h3>
            </v-col>
          </v-row>

          <!-- Pharmacy Field if edit and multiple branches / only on pharmacy detail page -->
          <v-row v-if="isEditMode && pharmacy" class="mb-4">
            <v-col cols="12">
              <v-select
                v-model="formData.pharmacy_id"
                :items="pharmacyOptions"
                item-text="label"
                item-value="_id"
                label="Filiale auswählen"
                :menu-props="{ bottom: true, offsetY: true }"
                class="pharmacy-select"
              />
            </v-col>
          </v-row>

          <!-- Services Field  -->
          <v-row>
            <v-col cols="12">
              <v-select
                :menu-props="{ bottom: true, offsetY: true }"
                label="mögliche Dienstleistungen*"
                v-model="formData.available_services"
                :items="serviceOptions"
                multiple
                :rules="[validateServiceSelect]"
                class="service-dropdown mt-4"
              ></v-select>
            </v-col>
          </v-row>

          <!-- Date Field -->
          <v-row>
            <v-col cols="12">
              <v-menu
                v-model="isDateMenuOpen"
                :close-on-content-click="true"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                :disabled="appointment?.wf_state == 'invoice' || appointment?.wf_state == 'tempInvoice'"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dateFormatted"
                    label="Datum*"
                    id="date"
                    v-on="on"
                    required
                    :rules="requiredRules"
                    :hint="holidays"
                    persistent-hint
                    autocomplete="off"
                    class="stick-left"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="formData.date"
                  no-title
                  locale="de-de"
                  :first-day-of-week="1"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <!-- Billing Field -->
          <v-row>
            <v-col cols="12">
              <v-select
                label="Art der Abrechnung"
                class="billing"
                :items="billingOptions"
                v-model="formData.billing"
              />
            </v-col>
          </v-row>

          <v-row class="align-center">
            <v-col cols="12">
              <appointment-time-dialog
                :defaultTimeSnippet="defaultTimeSnippet"
                v-model="formData"
              >
              </appointment-time-dialog>
            </v-col>
          </v-row>

          <!-- Travel-cost correction -->
          <v-row v-if="isEditMode">
            <v-col cols="12">
              <v-text-field
                v-model.number="formData.travelcosts"
                type="number"
                min="0"
                step="0.01"
                label="Fahrtkosten"
                suffix="€"
                class="stick-left travelcosts"
              />
            </v-col>
          </v-row>

          <!-- Mediation-charges Field -->
          <v-row>
            <v-col cols="12" md="12" sm="12">
              <v-text-field
                v-model.number="formData.mediation_charge"
                type="number"
                min="0"
                label="Vermittlungsaufschlag / Rabatt"
                hint="für Flying Pharmacist"
                persistent-hint
                suffix="€"
                class="stick-left mediation-charge"
              />
            </v-col>
          </v-row>

          <!-- Mediation-charges Field -->
          <v-row>
            <v-col cols="12" md="12" sm="12">
              <v-text-field
                v-model.number="formData.mediation_charge_description"
                label="Vermittlungsaufschlag / Rabatt Erläuterung"
                hint="für Flying Pharmacist"
                persistent-hint
                class="stick-left"
              />
              </v-col>
          </v-row>



          <!-- Other-charges Field -->
          <v-row>
            <v-col cols="12" md="12" sm="12">
              <v-text-field
                v-model.number="formData.other_costs"
                type="number"
                min="0"
                label="sonstige Kosten Honorarbestandteil"
                hint="für Freiberufler"
                suffix="€"
                persistent-hint
                class="stick-left"
              />
            </v-col>
          </v-row>

          <!-- Other-costs-explanation Field -->
          <v-row>
            <v-col cols="12" md="12" sm="12">
              <v-text-field
                v-model="formData.other_costs_explanation"
                label="Erläuterung"
                hint="zu 'sonstige Kosten Honorarbestandteil' für Freiberufler"
                persistent-hint
                class="stick-left"
              />
            </v-col>
          </v-row>

          <!-- Comments Field -->
          <v-row>
            <v-col cols="12" md="12" sm="12">
              <v-textarea
                v-model="formData.comment"
                hint="(bitte keine Einträge vornehmen)"
                label="Kommentar von der Apotheke"
                persistent-hint
                rows="2"
              />
            </v-col>
          </v-row>

          <!-- Internal comments Field -->
          <v-row>
            <v-col cols="12" md="12" sm="12">
              <v-textarea
                v-model="formData.internal_comment"
                hint="nur für interne Kommunikation"
                label="Interner Kommentar"
                persistent-hint
                rows="2"
              />
            </v-col>
          </v-row>

          <!-- FB Comment Field -->
          <v-row>
            <v-col cols="12" md="12" sm="12">
              <v-textarea
                rows="2"
                v-model="formData.comment_to_fb"
                label="Kommentar für Freiberufler"
                persistent-hint
              />
            </v-col>
          </v-row>

          <!-- Other Fields -->
          <v-row>
            <v-col cols="12">
              <v-checkbox
                v-if="!isEditMode"
                id="nightshift"
                v-model="formData.nightshift"
                label="Nachtschicht?"
                hide-details
              />
              <v-checkbox
                v-model="formData.commited"
                id="committed"
                label="feste Zusage?"
                hide-details
              />
              <v-checkbox
                :disabled="pharmacy.is_angel"
                v-model="formData.is_angel"
                label="Engel?"
                hide-details
              />
              <v-checkbox
                v-model="formData.hba"
                label="HBA zwingend erforderlich?"
                hide-details
              />
              <v-checkbox
                v-if="canToggleAllowDirectApproval"
                v-model="formData.allow_direct_approval"
                label="Erlaube direkte Zusage für diesen Termin"
                hide-details
              />
            </v-col>
          </v-row>

          <!-- Invoice Approval (User State) Field -->
          <v-row v-if="isEditMode && !['inquired', 'open', 'allocated'].includes(appointment.wf_state)">
            <v-col cols="12">
              <v-select
                label="Rechnungsfreigabe"
                class="invoice-approval-select"
                :items="appointmentApprovalOptions"
                v-model="formData.user_state"
                hide-details
              />
            </v-col>
          </v-row>

          <!-- Awaiting comments section -->
          <v-row v-if="isEditMode && awaitingCommentsRows.length">
            <v-col cols="12">
              <p class="mb-1 subtitle-1">Kommentare zur Rechnungsfreigabe</p>
              <div>
                <!-- Loop over awaiting_comments object to display label: value -->
                <div v-for="(row) in awaitingCommentsRows" :key="row.label">
                  <strong class="py-2">{{ row.label }}:</strong> {{ row.comment }}
                </div>
              </div>

              <div class="mt-5" v-if="appointment.initial_time_details && appointment.fb_time_details">
                <p class="mb-1">
                  Neue FB Zeit: {{ appointment.fb_time_details.display_time }} ({{ appointment.fb_time_details.hours }} Stunden)
                </p>
                <p class="mb-1">
                  Ursprüngliche Zeit: {{ appointment.initial_time_details.display_time }} ({{ appointment.initial_time_details.hours }} Stunden)
                </p>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="!isEditMode">
            <v-col cols="12">
              <v-checkbox
                v-model="formData.hidden"
                label="Termin verstecken?"
                class="hide-appointment"
                hide-details
              />
            </v-col>
          </v-row>

          <!-- Action buttons -->
          <v-row class="mt-8">
            <v-col>
              <v-btn
                @click="onSaveButtonClicked"
                :disabled="!valid || saving"
                :loading="saving"
                class="save-appointment"
                id="submit-apt"
                color="primary"
              >
                Speichern
              </v-btn>
              <v-btn class="close-drawer" @click="isDrawerOpen = false"
                >Abbrechen</v-btn
              >
              <AppointmentsClashesDialog
                :clashes="clashes"
                :appointments="[formData]"
                @confirm="onSaveButtonClicked"
                @abort="onClashDialogAbort"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-navigation-drawer>
  </v-sheet>
</template>

<script>
import moment from "moment";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { mapMutations } from "vuex";
import AppointmentTimeDialog from "./AppointmentTimeDialog.vue";
import AppointmentsClashesDialog from "./AppointmentsClashesDialog.vue";
import { AppointmentManagerDefaults } from "../../constants";
const TIME_FORMAT = "HH:mm";
const NIGHTSHIFT_DEFAULTS = AppointmentManagerDefaults.nightshift;
const DEFAULT_TIME_DETAILS = {
  additional_information: "",
  break_duration: 0,
  break_has_fixed_time: null,
  break_start_time: null,
  paid_break: false
}

export default {
  props: {
    appointment: Object,
    pharmacy: Object,
  },
  components: {
    AppointmentTimeDialog,
    AppointmentsClashesDialog
  },
  data() {
    return {
      isDrawerOpen: false,
      saving: false,
      creationRequestId: null,
      creationAttemptToken: 0,
      clashCheckedFingerprint: null,
      creationPostStarted: false,
      proposalWarning: null,
      resumingProposals: false,
      isDateMenuOpen: false,
      serviceOptions: AppointmentManagerDefaults.serviceOptions,
      billingOptions: AppointmentManagerDefaults.billingOptions,
      appointmentApprovalOptions: AppointmentManagerDefaults.appointmentApprovalOptions,
      clashes: [],
      holidays: "",
      formData: {
        expected_group_exists: this.appointment ? this.appointment.expected_group_exists : undefined,
        expected_group_version: this.appointment ? this.appointment.expected_group_version : undefined,
        pharmacy_id: this.pharmacy?._id
          || this.pharmacy?.pharmacy_id
          || this.pharmacy?.id
          || this.appointment?.pharmacy_id
          || null,
        begin: this.appointment ? this.formatTime(this.appointment.begin) : "08:00",
        end: this.appointment ? this.formatTime(this.appointment.end) : "18:00",
        date: this.appointment ? this.appointment.date : "",
        time: this.appointment
          ? (this.appointment.display_time || this.appointment.time || this.appointment.time_details?.additional_information || "08.00 - 18.00 Uhr")
          : "08.00 - 18.00 Uhr",
        available_services: this.appointment ? this.appointment.available_services : ["substitution"],
        filtered_available_services: this.appointment ? this.appointment.filtered_available_services : [],
        service_categories: this.appointment ? this.appointment.service_categories : "",
        billing: this.appointment ? this.appointment.billing.type : "hours",
        quantity: this.appointment ? this.appointment.quantity : 10,
        travelcosts: this.appointment ? this.appointment.travelcosts : 0,
        mediation_charge: this.appointment ? this.appointment.mediation_charge : 0,
        mediation_charge_description: this.appointment ? this.appointment.mediation_charge_description : "",
        other_costs: this.appointment ? this.appointment.other_costs : 0,
        other_costs_explanation: this.appointment ? this.appointment.other_costs_explanation : "",
        comment: this.appointment ? this.appointment.comment : "",
        internal_comment: this.appointment ? this.appointment.internal_comment : "",
        comment_to_fb: this.appointment ? this.appointment.comment_to_fb : "",
        nightshift: this.appointment ? this.appointment.nightshift : false,
        commited: this.appointment ? this.appointment.commited : false,
        hba: this.appointment ? this.appointment.hba : false,
        hidden: this.appointment ? this.appointment.hidden : false,
        time_details: this.appointment?.time_details ? {
          ...this.appointment.time_details
        } : {},
        user_state: this.appointment ? this.appointment.user_state : "",
        allow_direct_approval: this.appointment ? this.appointment.allow_direct_approval: false,
      },
      valid: true,
      requiredRules: [(v) => !!v || "Dieses Feld ist ein Pflichtfeld"],
      hoursRules: [(v) => (!!v && v > 0) || "Bitte die Stunden angeben"],
      initialFormData: null,
    }
  },
  created() {
    this.initialFormData = JSON.parse(JSON.stringify(this.formData));
  },
  computed: {
    selectedPharmacyId() {
      return this.formData.pharmacy_id;
    },
    pharmacyOptions() {
      return (this.pharmacy?.branches || []).map(branch => ({
        ...branch,
        label: branch.shortname,
      }));
    },
    isEditMode() {
      return !!this.appointment; // Determines the mode based on the presence of appointment
    },
    dateFormatted() {
      if (!this.formData.date) return null;
      return moment(this.formData.date, "YYYY-MM-DD").format("DD.MM.YYYY");
    },
    canToggleAllowDirectApproval() {
      return !!this.appointment
        && this.pharmacy.no_approval_required === false
        && ((this.appointment.group_id && this.appointment.group_first) || !this.appointment.group_id);
    },
    awaitingCommentsRows() {
      return Array.isArray(this.appointment?.awaiting_comments)
        ? this.appointment.awaiting_comments
        : [];
    },
    defaultTimeSnippet() {
      const d = moment(this.formData.date, "YYYY-MM-DD");
      const selectedDay = d.day() == 0 ? 7 : d.day();
      return this.pharmacy["snippet" + selectedDay] ?? "";
    },
    date: {
      get() {
        return this.formData.date;
      },
      set(value) {
        this.formData.date = moment(value).format("YYYY-MM-DD");
      },
    },
    pharmacyURL() {
      return this.isEditMode
        ? `/pharmacies/${this.pharmacy.pharmacy_id}`
        : `/pharmacies/${this.pharmacy._id}`
    },
    ActionURL() {
      let URLPATH = !this.isEditMode
        ? "/add_appointment"
        : "/" + this.appointment._id + "/edit";
      return `${this.pharmacyURL}${URLPATH}`;
    },
  },
  methods: {
    validateServiceSelect(value) {
      if (value && value.length) return true;
      return "Bitte wählen Sie mindestens eine Dienstleistungsart aus";
    },
    /** Only applicable to batch-add, not to batch-edit */
    async getDateClashesWithExistingAppointments() {
      const dates = [moment(this.formData.date).format("DD.MM.YYYY")];
      const clashesResponse = await axios.post(
        `${this.pharmacyURL}/check_batch_dates`,
        { dates, ids: [] }
      );
      if (clashesResponse.data.status == "wait") {
        return clashesResponse.data.clashes;
      }
      return null;
    },
    onClashDialogAbort() {
      this.clashes = [];
      this.clashCheckedFingerprint = null;
    },
    async onSaveButtonClicked() {
      if (this.saving) return;
      if (!this.$refs.form.validate()) {
        return;
      }
      this.saving = true;
      const attemptToken = ++this.creationAttemptToken;
      if (!this.isEditMode && !this.creationRequestId) {
        this.creationRequestId = uuidv4();
      }
      if (!this.isEditMode) {
        const fingerprint = JSON.stringify(this.formData);
        if (this.clashCheckedFingerprint !== fingerprint) {
          try {
            const clashes = await this.getDateClashesWithExistingAppointments();
            if (attemptToken !== this.creationAttemptToken || !this.isDrawerOpen) return;
            this.clashCheckedFingerprint = fingerprint;
            this.clashes = clashes;
            if (this.clashes) return;
          } catch (error) {
            if (attemptToken === this.creationAttemptToken) {
              this.alertError(`Terminkonflikte konnten nicht geprüft werden: ${error}`);
              this.saving = false;
            }
            return;
          } finally {
            if (attemptToken === this.creationAttemptToken && this.clashes) {
              this.saving = false;
            }
          }
        }
      }
      await this.submitData(attemptToken);
    },
    async submitData(attemptToken = null) {
      if (this.saving && attemptToken === null) return;
      if (attemptToken === null) {
        this.saving = true;
        attemptToken = ++this.creationAttemptToken;
      }
      try {
        let response;
        const normalizeTravelcosts = (value) => (
          value === undefined || value === null || value === '' ? null : Number(value)
        );
        const travelcostsChanged = this.isEditMode
          && normalizeTravelcosts(this.formData.travelcosts)
            !== normalizeTravelcosts(this.initialFormData.travelcosts);
        const withoutTravelcosts = (data) => {
          const value = JSON.parse(JSON.stringify(data));
          delete value.travelcosts;
          return value;
        };
        const hasOtherChanges = JSON.stringify(withoutTravelcosts(this.formData))
          !== JSON.stringify(withoutTravelcosts(this.initialFormData));

        if (travelcostsChanged && !hasOtherChanges) {
          response = await axios.post(
            `/api/appointments/${this.appointment._id}/time_correction`,
            {
              travelcosts: this.formData.travelcosts,
              expected_group_version: this.formData.expected_group_version,
            }
          );
        } else {
          if (!this.isEditMode) this.creationPostStarted = true;
          response = await axios.post(this.ActionURL, {
            ...this.formData,
            ...(!this.isEditMode ? { request_id: this.creationRequestId } : {}),
          });
        }
        const groupId = String(this.appointment?.group_id || "");
        const committedVersion = response?.data?.expected_group_version
          || response?.data?.group_versions?.[groupId];
        if (committedVersion) {
          this.$set(this.appointment, 'expected_group_version', committedVersion);
          this.formData.expected_group_version = committedVersion;
          this.initialFormData.expected_group_version = committedVersion;
        }
        if (response?.data?.warning) this.proposalWarning = response.data.warning;
        this.creationRequestId = null;
        this.clashCheckedFingerprint = null;
        this.creationPostStarted = false;
        this.isDrawerOpen = false;
        this.$emit('fetch-appointments');
        if (!response?.data?.warning) this.alertSuccess('Termin gespeichert');
      } catch (error) {
        const isCreationRequestConflict = !this.isEditMode
          && error?.response?.status === 409
          && error?.response?.data?.code === 'creation_request_conflict';
        if (isCreationRequestConflict) {
          this.creationRequestId = uuidv4();
          this.creationPostStarted = false;
          this.clashCheckedFingerprint = null;
          this.$emit('fetch-appointments');
          this.alertError(
            'Der vorherige Termin wurde bereits gespeichert. Die Liste wurde aktualisiert; Änderungen können jetzt als neuer Termin gespeichert werden.'
          );
          return;
        }
        const errorMessage = error?.response?.data?.message
          || error?.response?.data?.error
          || error;
        this.alertError(
          `Termine konnten nicht gespeichert werden: ${errorMessage}`
        );
      } finally {
        if (attemptToken === this.creationAttemptToken) this.saving = false;
      }
    },
    async resumeProposals() {
      if (this.resumingProposals) return;
      this.resumingProposals = true;
      try {
        for (const appointmentId of this.proposalWarning.appointment_ids) {
          const response = await axios.get(`/api/appointments/${appointmentId}/enable_automated_proposals/?resend=false`);
          const payload = Array.isArray(response.data) ? response.data[0] : response.data;
          const status = Array.isArray(response.data) ? response.data[1] : response.status;
          if (status >= 300 || payload?.error) throw new Error(payload?.error || 'Vorschläge konnten nicht gestartet werden.');
        }
        this.proposalWarning = null;
        this.alertSuccess('Vorschläge erfolgreich gestartet.');
      } catch (error) {
        this.alertError(error?.response?.data?.error || error.message);
      } finally {
        this.$emit('fetch-appointments');
        this.resumingProposals = false;
      }
    },
    formatHoliday(holiday) {
      if (holiday.type == "school") {
        return "Schulferien";
      } else if (holiday.special) {
        return `Sonderfeiertag: ${holiday.name}`;
      } else {
        return `Feiertag: ${holiday.name}`;
      }
    },
    formatHolidays(holidays) {
      return holidays.map((h) => this.formatHoliday(h)).join(", ");
    },

    getWeekDay(date) {
      return moment(date).day() || 7; // Map Sunday from 0 to 7 so that pharmacy['begin7']/end7 etc can be accessed
    },
    getPharmacyWeekdayDefaults(selector, date) {
      const weekday = this.getWeekDay(date);
      return this.pharmacy[`${selector}${weekday}`];
    },
    applyNightshiftDefaults() {
      this.formData.billing = NIGHTSHIFT_DEFAULTS.billing;
      this.formData.begin = NIGHTSHIFT_DEFAULTS.begin;
      this.formData.end = NIGHTSHIFT_DEFAULTS.end;
      this.formData.time = NIGHTSHIFT_DEFAULTS.time;
      this.formData.quantity = NIGHTSHIFT_DEFAULTS.quantity;
    },
    formatTime(value) {
      if (!value) return null;

      // Detect if timezone exists (Z or +hh:mm / -hh:mm)
      const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(value);

      return hasTimezone
        ? moment.parseZone(value).format(TIME_FORMAT)  // preserve timezone
        : moment(value).format(TIME_FORMAT);           // fallback (old behavior)
  },
    ...mapMutations({
      alertError: "alert/error",
      alertSuccess: "alert/success"
    }),
  },

  watch: {
    date: async function (newVal) {
      if (this.isDrawerOpen) {
        const URL = `/holidays/${newVal}`;
        const response = await axios(`${this.pharmacyURL}${URL}`);
        const text = this.formatHolidays(response.data);
        this.holidays = text;
      }
    },
    isDateMenuOpen: function (newVal) {
      if (!this.isEditMode && newVal && !this.formData.date) {
        const today = moment().format("YYYY-MM-DD");
        this.$set(this.formData, "date", today);
      }
    },

    "formData.nightshift"(isNightShift) {
      if (isNightShift) {
        this.applyNightshiftDefaults();
      } else {
        this.formData.billing = "hours";
        this.formData.begin = "08:00";
        this.formData.end = "18:00";
        this.formData.time = "08.00 - 18.00 Uhr";
      }
      this.formData.time_details = { ...DEFAULT_TIME_DETAILS };
    },
    "formData.date"(date) {
      if (this.formData.nightshift) {
        this.applyNightshiftDefaults();
        return;
      }

      const beginDate = this.getPharmacyWeekdayDefaults('begin', date) || '1900-01-01T08:00:00';
      const endDate = this.getPharmacyWeekdayDefaults('end', date) || '1900-01-01T18:00:00';
      const time = this.getPharmacyWeekdayDefaults('snippet', date) || "08.00 - 18.00 Uhr";
      const quantity = this.getPharmacyWeekdayDefaults('hours', date) || 10;
      this.formData.begin = moment.utc(beginDate).format(TIME_FORMAT);
      this.formData.end = moment.utc(endDate).format(TIME_FORMAT);
      this.formData.time = time;
      this.formData.quantity = quantity;
    },
    appointment: {
      handler(apt) {
        this.formData.available_services = apt.available_services;
        this.formData.filtered_available_services = apt.filtered_available_services;
        this.formData.service_categories = apt.service_categories;
        this.formData.commited = apt.commited;
      },
      deep: true,
    },
    isDrawerOpen(newVal) {
      if (newVal) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
        this.creationAttemptToken += 1;
        this.saving = false;
        if (!this.creationPostStarted) {
          this.creationRequestId = null;
          this.clashCheckedFingerprint = null;
        }
      }
    },
  },
};
</script>

<style scoped>
.edit-appointment {
  background: #5bc0de !important;
  min-height: 30px !important;
  height: 30px !important;
  min-width: 30px !important;
  padding: 0 10px !important;
}
.vuetify-wrapper .save-appointment {
  margin-right: 10px;
}
.time .vc-container .vc-time-picker .vc-date {
  display: none !important;
}
.apt-manager {
  padding: 48px;
}
.v-menu__content {
  width: auto !important;
  min-width: auto !important;
}
.vuetify-wrapper .v-navigation-drawer--temporary {
  z-index: 9999;
}

</style>
